import { useEffect, useRef, useState } from '../lib/teact/teact';

import { STRICTERDOM_ENABLED } from '../config';
import { disableStrict, enableStrict } from '../lib/fasterdom/stricterdom';
import useFlag from './useFlag';

const CAPTCHA_ID = 'c1283dcc429fc505517e65e468817866';
const INIT_TIMEOUT_MS = 15000;
const READY_TIMEOUT_MS = 15000;
const STRICT_RESTORE_DELAY_MS = 300;

export interface GeeTestValidateResult {
  captcha_id: string;
  lot_number: string;
  pass_token: string;
  gen_time: string;
  captcha_output: string;
}

const GEETEST_CSS = `
  #geetest-captcha-wrapper .geetest_wrap,
  #geetest-captcha-wrapper .geetest_popup_main {
    background-color: white !important;
    border: 1px solid #ccc !important;
    border-radius: 4px !important;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1) !important;
  }
  #geetest-captcha-wrapper .geetest_btn,
  #geetest-captcha-wrapper .geetest_input {
    display: block !important;
    opacity: 1 !important;
  }
  #geetest-captcha-wrapper .geetest_item img {
    visibility: visible !important;
    opacity: 1 !important;
  }
`;

function isValidValidateResult(result: unknown): result is GeeTestValidateResult {
  if (!result || typeof result !== 'object') {
    return false;
  }

  const r = result as Record<string, unknown>;
  return Boolean(
    r.captcha_id
    && r.lot_number
    && r.pass_token
    && r.gen_time
    && r.captcha_output,
  );
}

export default function useGeeTestCaptcha(
  onSuccess: (validateResult: GeeTestValidateResult) => void,
) {
  const [isCaptchaLoading, markCaptchaLoading, unmarkCaptchaLoading] = useFlag();
  const [captchaError, setCaptchaError] = useState<string | undefined>();
  const isCaptchaInProgressRef = useRef(false);
  const captchaSessionIdRef = useRef(0);
  const initTimeoutRef = useRef<number | undefined>();
  const readyTimeoutRef = useRef<number | undefined>();
  const strictRestoreTimeoutRef = useRef<number | undefined>();
  const captchaObjRef = useRef<any>();
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  function clearInitTimeout() {
    if (initTimeoutRef.current !== undefined) {
      window.clearTimeout(initTimeoutRef.current);
      initTimeoutRef.current = undefined;
    }
  }

  function clearReadyTimeout() {
    if (readyTimeoutRef.current !== undefined) {
      window.clearTimeout(readyTimeoutRef.current);
      readyTimeoutRef.current = undefined;
    }
  }

  function clearStrictRestoreTimeout() {
    if (strictRestoreTimeoutRef.current !== undefined) {
      window.clearTimeout(strictRestoreTimeoutRef.current);
      strictRestoreTimeoutRef.current = undefined;
    }
  }

  function clearAllTimeouts() {
    clearInitTimeout();
    clearReadyTimeout();
    clearStrictRestoreTimeout();
  }

  function scheduleStrictRestore(sessionId: number) {
    if (!STRICTERDOM_ENABLED) {
      return;
    }

    clearStrictRestoreTimeout();
    strictRestoreTimeoutRef.current = window.setTimeout(() => {
      if (sessionId !== captchaSessionIdRef.current) {
        return;
      }
      enableStrict();
      strictRestoreTimeoutRef.current = undefined;
    }, STRICT_RESTORE_DELAY_MS);
  }

  function removeCaptchaContainer(sessionId: number) {
    clearStrictRestoreTimeout();

    try {
      captchaObjRef.current?.destroy?.();
    } catch {
      // Ignore SDK destroy errors
    }
    captchaObjRef.current = undefined;

    const container = document.getElementById('geetest-captcha-container');
    const wrapper = document.getElementById('geetest-captcha-wrapper');
    if (container?.parentNode) container.parentNode.removeChild(container);
    if (wrapper?.parentNode) wrapper.parentNode.removeChild(wrapper);

    scheduleStrictRestore(sessionId);
  }

  function resetCaptchaState(sessionId?: number) {
    if (sessionId !== undefined && sessionId !== captchaSessionIdRef.current) {
      return;
    }

    captchaSessionIdRef.current += 1;
    clearAllTimeouts();
    isCaptchaInProgressRef.current = false;
    unmarkCaptchaLoading();
    removeCaptchaContainer(captchaSessionIdRef.current);
  }

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = GEETEST_CSS;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    return () => {
      resetCaptchaState();
      try {
        captchaObjRef.current?.destroy?.();
      } catch {
        // Ignore
      }
      captchaObjRef.current = undefined;
      if (STRICTERDOM_ENABLED) {
        enableStrict();
      }
    };
  }, []);

  function requestCaptcha() {
    if (isCaptchaInProgressRef.current) {
      return false;
    }
    if (document.getElementById('geetest-captcha-wrapper')) {
      return false;
    }
    if (typeof window.initGeetest4 !== 'function') {
      setCaptchaError('CaptchaUnavailable');
      return false;
    }

    setCaptchaError(undefined);
    clearAllTimeouts();

    const sessionId = ++captchaSessionIdRef.current;
    isCaptchaInProgressRef.current = true;
    markCaptchaLoading();
    if (STRICTERDOM_ENABLED) {
      disableStrict();
    }

    const captchaContainer = document.createElement('div');
    captchaContainer.id = 'geetest-captcha-container';
    Object.assign(captchaContainer.style, {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: '9999',
      display: 'block',
      visibility: 'visible',
      opacity: '1',
    });

    const captchaWrapper = document.createElement('div');
    captchaWrapper.id = 'geetest-captcha-wrapper';
    captchaWrapper.style.cssText = `
      background-color: rgba(0, 0, 0, 0.2);
      width: 100%; height: 100%;
      position: fixed; top: 0; left: 0;
      z-index: 998; visibility: hidden;
    `;
    captchaWrapper.appendChild(captchaContainer);
    document.body.appendChild(captchaWrapper);

    initTimeoutRef.current = window.setTimeout(() => {
      if (sessionId !== captchaSessionIdRef.current) {
        return;
      }
      resetCaptchaState(sessionId);
      setCaptchaError('CaptchaUnavailable');
    }, INIT_TIMEOUT_MS);

    try {
      window.initGeetest4({
        captchaId: CAPTCHA_ID,
        product: 'popup',
        riskType: 'nine',
      }, (captchaObj: any) => {
        try {
          if (sessionId !== captchaSessionIdRef.current) {
            try { captchaObj?.destroy?.(); } catch { /* Ignore */ }
            return;
          }

          captchaObjRef.current = captchaObj;
          captchaObj.appendTo('#geetest-captcha-container');

          readyTimeoutRef.current = window.setTimeout(() => {
            if (sessionId !== captchaSessionIdRef.current) {
              return;
            }
            resetCaptchaState(sessionId);
            setCaptchaError('CaptchaUnavailable');
          }, READY_TIMEOUT_MS);

          captchaObj.onReady(() => {
            try {
              if (sessionId !== captchaSessionIdRef.current) {
                return;
              }

              clearInitTimeout();
              clearReadyTimeout();

              const c = document.getElementById('geetest-captcha-container');
              const w = document.getElementById('geetest-captcha-wrapper');
              if (c && w) {
                c.style.display = 'block';
                c.style.visibility = 'visible';
                c.style.opacity = '1';
                w.style.visibility = 'visible';
              }
            } catch {
              resetCaptchaState(sessionId);
              setCaptchaError('CaptchaUnavailable');
            }
          });

          captchaObj.onSuccess(() => {
            try {
              if (sessionId !== captchaSessionIdRef.current) {
                return;
              }

              clearAllTimeouts();
              const validateResult = captchaObj.getValidate();
              if (isValidValidateResult(validateResult)) {
                isCaptchaInProgressRef.current = false;
                unmarkCaptchaLoading();
                removeCaptchaContainer(sessionId);
                onSuccessRef.current(validateResult);
              } else {
                resetCaptchaState(sessionId);
                setCaptchaError('CaptchaUnavailable');
              }
            } catch {
              resetCaptchaState(sessionId);
              setCaptchaError('CaptchaUnavailable');
            }
          });

          captchaObj.onError(() => {
            if (sessionId !== captchaSessionIdRef.current) {
              return;
            }
            resetCaptchaState(sessionId);
            setCaptchaError('CaptchaUnavailable');
          });

          captchaObj.onClose(() => {
            if (sessionId !== captchaSessionIdRef.current) {
              return;
            }
            resetCaptchaState(sessionId);
          });
        } catch {
          resetCaptchaState(sessionId);
          setCaptchaError('CaptchaUnavailable');
        }
      });
    } catch {
      resetCaptchaState(sessionId);
      setCaptchaError('CaptchaUnavailable');
      return false;
    }

    return true;
  }

  function clearCaptchaError() {
    setCaptchaError(undefined);
  }

  return {
    isCaptchaLoading,
    captchaError,
    requestCaptcha,
    clearCaptchaError,
  };
}
